/**
 * Drives the robot's switch directly over Wi-Fi, with or without data. The switch's own script is
 * the witness: it measures how long every bulb really stayed on, and a cycle counts as completed
 * only when its record says so. Every cycle is saved on the tablet (and on the switch) until the
 * server has it.
 */
import type { SyncCycle } from '@evesix/contracts';
import type { AbortReason, CycleJob, CycleRecord } from '../domain/types';
import { findSwitch, type LocalAddress } from './discovery';
import type { LocalStore } from './local-store';
import {
  CycleRecordError,
  StopNotAcknowledgedError,
  type LampCommand,
  type LampCommandResult,
  type RobotGateway,
  type RobotStatus,
} from './robot';
import type { SessionState } from './session';
import { SwitchClient, SwitchError, type HttpTransport } from './switch';
import type { SyncService } from './sync';

/** A record from the switch script's log. */
export interface SwitchRun {
  readonly seq: number;
  readonly run: string | null;
  readonly kind: 'cycle' | 'test' | 'unarmed' | 'blocked';
  readonly outcome: 'completed' | 'cut' | 'manual' | 'blocked';
  readonly reason: string | null;
  readonly detail: string | null;
  readonly outputs: readonly number[];
  readonly seconds: number | null;
  readonly onMs: readonly number[];
  readonly endedUnix: number | null;
}

interface LogPage {
  readonly active: { readonly run: string | null } | null;
  readonly done: readonly SwitchRun[];
  readonly more: boolean;
}

/** The cycle this tablet started and has not yet recorded; kept through reloads. */
export interface ActiveRun {
  readonly id: string;
  readonly job: CycleJob;
  readonly operatorId: number;
  readonly startedAt: string;
  readonly tabletUptimeMs: number;
}

const ACTIVE_KEY = 'cycle.active';
const ADDRESS_KEY = 'switch.address';
/** Seconds each output's own auto-off timer runs past the planned time. */
const RELAY_GRACE_SECONDS = 5;

const ABORT_REASONS: readonly string[] = [
  'MANUAL_EMERGENCY_STOP',
  'OCCUPANCY_BREACH',
  'DEVICE_FAULT',
  'LAMP_INTERRUPTED',
];

/** What the operator is told when the switch cut a cycle short. */
export function abortReasonOf(run: Pick<SwitchRun, 'reason' | 'detail'>): AbortReason {
  switch (run.reason) {
    case 'STOPPED':
      return ABORT_REASONS.includes(run.detail ?? '')
        ? (run.detail as AbortReason)
        : 'MANUAL_EMERGENCY_STOP';
    case 'DEVICE_PROTECTION':
    case 'OUTPUT_NOT_ON':
    case 'LAMP_UNDERPOWERED':
      return 'DEVICE_FAULT';
    default:
      return 'LAMP_INTERRUPTED'; // SWITCHED_OFF, POWER_LOST, NOT_WATCHED
  }
}

const UNKNOWN: RobotStatus = Object.freeze({
  connected: false,
  lampOn: null,
  lampPowerW: null,
  manualAutoOffAt: null,
});

export interface DirectRobotOptions {
  readonly store: LocalStore;
  readonly sync: SyncService;
  readonly session: SessionState;
  readonly transport: HttpTransport;
  readonly localAddresses: () => Promise<readonly LocalAddress[]>;
  readonly now?: () => number;
  /** Time since the tablet started; cannot be changed by hand. */
  readonly uptime?: () => number;
  readonly uuid?: () => string;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly pollMs?: number;
  /** How long to wait for the switch's record once the cycle's time is up. */
  readonly verdictTimeoutMs?: number;
  /** Least time between network searches while the switch cannot be found. */
  readonly rediscoverMs?: number;
}

export class DirectRobotGateway implements RobotGateway {
  readonly #store: LocalStore;
  readonly #sync: SyncService;
  readonly #session: SessionState;
  readonly #transport: HttpTransport;
  readonly #localAddresses: () => Promise<readonly LocalAddress[]>;
  readonly #now: () => number;
  readonly #uptime: () => number;
  readonly #uuid: () => string;
  readonly #sleep: (ms: number) => Promise<void>;
  readonly #pollMs: number;
  readonly #verdictTimeoutMs: number;
  readonly #rediscoverMs: number;
  readonly #listeners = new Set<() => void>();
  readonly #lostListeners = new Set<(reason: AbortReason) => void>();
  /** Completed verdicts for our run, held until completeCycle records them. */
  readonly #verdicts = new Map<string, SwitchRun>();
  readonly ready: Promise<void>;
  #status: RobotStatus = UNKNOWN;
  #client: SwitchClient | null = null;
  /** Which switch (device id and password) `#client` was made for. */
  #clientFor: string | null = null;
  /** The network search under way, shared by every caller that needs the switch meanwhile. */
  #searching: Promise<SwitchClient | null> | null = null;
  /** The poll under way: a slow switch must not have two reads of its log filing the same record. */
  #refreshing: Promise<void> | null = null;
  #active: ActiveRun | null = null;
  /** The run recorded last, in case its result arrives before completeCycle asks for it. */
  #lastFinished: { readonly id: string; readonly verdict: SwitchRun | null } | null = null;
  #lastSearch = Number.NEGATIVE_INFINITY;
  #manualAutoOffAt: number | null = null;
  #timer: ReturnType<typeof setInterval> | undefined;

  constructor({
    store,
    sync,
    session,
    transport,
    localAddresses,
    now = Date.now,
    uptime = () => performance.now(),
    uuid = () => crypto.randomUUID(),
    sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    pollMs = 3000,
    verdictTimeoutMs = 30_000,
    rediscoverMs = 30_000,
  }: DirectRobotOptions) {
    this.#store = store;
    this.#sync = sync;
    this.#session = session;
    this.#transport = transport;
    this.#localAddresses = localAddresses;
    this.#now = now;
    this.#uptime = uptime;
    this.#uuid = uuid;
    this.#sleep = sleep;
    this.#pollMs = pollMs;
    this.#verdictTimeoutMs = verdictTimeoutMs;
    this.#rediscoverMs = rediscoverMs;
    this.ready = this.#load();
  }

  async #load(): Promise<void> {
    this.#active = (await this.#store.get<ActiveRun>(ACTIVE_KEY)) ?? null;
  }

  /** The cycle running on the switch that this tablet started, to rejoin after a reload. */
  activeRun(): ActiveRun | null {
    return this.#active;
  }

  getStatus(): RobotStatus {
    return this.#status;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    if (this.#listeners.size === 1) {
      void this.refresh();
      this.#timer = setInterval(() => void this.refresh(), this.#pollMs);
    }
    return () => {
      this.#listeners.delete(listener);
      if (this.#listeners.size === 0) clearInterval(this.#timer);
    };
  }

  onCycleLost(listener: (reason: AbortReason) => void): () => void {
    this.#lostListeners.add(listener);
    return () => this.#lostListeners.delete(listener);
  }

  /** Reads the switch once: lamp state, and any records it has made since. */
  refresh(): Promise<void> {
    this.#refreshing ??= this.#refresh().finally(() => {
      this.#refreshing = null;
    });
    return this.#refreshing;
  }

  async #refresh(): Promise<void> {
    await this.ready;
    const sw = await this.#connect(false);
    if (!sw) {
      this.#setStatus({ ...this.#status, connected: false });
      return;
    }
    try {
      await this.#readLamp(sw);
      await this.#collect(sw);
    } catch (error) {
      this.#lostSwitch(error);
    }
  }

  async startEmission(job: CycleJob): Promise<void> {
    await this.ready;
    const pack = this.#sync.pack;
    const session = this.#session.current;
    if (!pack || !session) throw new Error('This tablet is not set up for the robot yet');
    const sw = await this.#requireSwitch();
    const id = this.#uuid();
    const seconds = Math.ceil(job.wallClockSeconds);
    const armed = await sw.script<{ error?: string }>('arm', {
      run: id,
      kind: 'cycle',
      outputs: job.channels.join(','),
      seconds,
      minW: Math.floor(pack.robot.minPowerW),
    });
    if (armed.status !== 200) throw new Error(`The switch refused the cycle: ${armed.body.error}`);
    try {
      for (const output of job.channels) {
        await sw.rpc('Switch.Set', {
          id: output,
          on: true,
          toggle_after: seconds + RELAY_GRACE_SECONDS,
        });
        // Lit from the first output on: the status bar must not say the lamp is off meanwhile.
        this.#setStatus({ ...this.#status, connected: true, lampOn: true, manualAutoOffAt: null });
      }
    } catch (error) {
      await this.#switchOff(sw, 'DEVICE_FAULT').catch(() => undefined);
      throw error;
    }
    this.#active = {
      id,
      job,
      operatorId: session.operatorId,
      startedAt: new Date(this.#now()).toISOString(),
      tabletUptimeMs: Math.round(this.#uptime()),
    };
    await this.#store.put(ACTIVE_KEY, this.#active);
    await this.#event('cycle_started', session.operatorId, id, {
      wardCode: job.wardCode,
      bed: job.bed,
      channels: job.channels,
    });
    this.#setStatus({ ...this.#status, connected: true, lampOn: true, manualAutoOffAt: null });
  }

  resumeCycle(): void {
    // The run is already loaded from the tablet's database; the next poll reads its state.
    this.#setStatus({ ...this.#status, lampOn: true, manualAutoOffAt: null });
  }

  /**
   * The cycle's time is up: waits for the switch's own record (the relay's timer ends it 5 s
   * after the tablet's countdown). Completed only when the switch measured the full time.
   */
  async completeCycle(record: CycleRecord): Promise<CycleRecord | null> {
    await this.ready;
    const active = this.#active;
    let verdict: SwitchRun | null;
    if (active) {
      verdict = await this.#awaitVerdict(active.id, this.#verdictTimeoutMs);
      await this.#finish(active, verdict, null);
    } else if (this.#lastFinished) {
      verdict = this.#lastFinished.verdict; // recorded already, while the countdown ran out
    } else {
      throw new CycleRecordError('unreachable', 'This tablet has no cycle in progress');
    }
    if (verdict?.outcome === 'completed') return record;
    if (verdict) {
      throw new CycleRecordError('rejected', `The switch cut the cycle short: ${verdict.reason}`);
    }
    throw new CycleRecordError(
      'unreachable',
      'The switch could not be reached to confirm the cycle',
    );
  }

  async emergencyStop(reason: AbortReason): Promise<void> {
    await this.ready;
    const sw = await this.#connect(true);
    if (!sw) throw new StopNotAcknowledgedError('The robot’s switch cannot be reached');
    await this.#switchOff(sw, reason);
    const active = this.#active;
    if (active) await this.#finish(active, await this.#awaitVerdict(active.id, 5000), reason);
    await this.#event(
      'emergency_stop',
      this.#session.current?.operatorId ?? null,
      active?.id ?? null,
      {
        reason,
      },
    );
  }

  async setLamp(command: LampCommand): Promise<LampCommandResult> {
    await this.ready;
    const sw = await this.#requireSwitch();
    const operatorId = this.#session.current?.operatorId ?? null;
    if (!command.on) {
      await this.#switchOff(sw, 'MANUAL_EMERGENCY_STOP');
      const active = this.#active;
      if (active) {
        await this.#finish(
          active,
          await this.#awaitVerdict(active.id, 5000),
          'MANUAL_EMERGENCY_STOP',
        );
      }
      await this.#event('manual_lamp_off', operatorId, active?.id ?? null, {});
      return { acknowledged: true, lampOn: false, autoOffAt: null };
    }
    const pack = this.#sync.pack;
    if (!pack) throw new Error('This tablet is not set up for the robot yet');
    const armed = await sw.script<{ error?: string }>('arm', {
      run: this.#uuid(),
      kind: 'test',
      outputs: pack.robot.outputs.join(','),
      seconds: command.seconds,
    });
    if (armed.status !== 200)
      throw new Error(`The switch refused the lamp test: ${armed.body.error}`);
    for (const output of pack.robot.outputs) {
      await sw.rpc('Switch.Set', { id: output, on: true, toggle_after: command.seconds });
    }
    this.#manualAutoOffAt = this.#now() + command.seconds * 1000;
    await this.#event('manual_lamp_on', operatorId, null, { seconds: command.seconds });
    this.#setStatus({ ...this.#status, lampOn: true, manualAutoOffAt: this.#manualAutoOffAt });
    return { acknowledged: true, lampOn: true, autoOffAt: this.#manualAutoOffAt };
  }

  // ── switch ──────────────────────────────────────────────────────────────────

  async #requireSwitch(): Promise<SwitchClient> {
    const sw = await this.#connect(true);
    if (!sw) throw new Error('The robot’s switch cannot be found on the network');
    return sw;
  }

  /** The switch client, searching the network for it when needed (not too often unless forced). */
  async #connect(force: boolean): Promise<SwitchClient | null> {
    const pack = this.#sync.pack;
    // The tablet was reset or set up for another robot, or the switch's password changed.
    const wanted = pack ? `${pack.robot.shellyDeviceId}|${pack.robot.switchPassword}` : null;
    if (this.#clientFor !== wanted) {
      this.#client = null;
      this.#clientFor = null;
    }
    if (this.#client) return this.#client;
    if (this.#searching) return this.#searching;
    if (!pack?.robot.shellyDeviceId) return null;
    if (!force && this.#now() - this.#lastSearch < this.#rediscoverMs) return null;
    this.#lastSearch = this.#now();
    this.#searching = this.#search(pack.robot.shellyDeviceId, pack.robot.switchPassword).finally(
      () => {
        this.#searching = null;
      },
    );
    return this.#searching;
  }

  async #search(deviceId: string, password: string | null): Promise<SwitchClient | null> {
    const address = await findSwitch({
      deviceId,
      known: (await this.#store.get<string>(ADDRESS_KEY)) ?? null,
      localAddresses: this.#localAddresses,
      transport: this.#transport,
    });
    if (!address) return null;
    await this.#store.put(ADDRESS_KEY, address);
    this.#client = new SwitchClient(address, password, this.#transport);
    this.#clientFor = `${deviceId}|${password}`;
    return this.#client;
  }

  #lostSwitch(error: unknown): void {
    if (error instanceof SwitchError && error.kind === 'unreachable') this.#client = null;
    this.#setStatus({ ...this.#status, connected: false });
  }

  async #readLamp(sw: SwitchClient): Promise<{ lampOn: boolean }> {
    const status =
      await sw.rpc<Record<string, { output?: boolean; apower?: number } | undefined>>(
        'Shelly.GetStatus',
      );
    const outputs = this.#sync.pack?.robot.outputs ?? [];
    const readings = outputs.map((o) => status[`switch:${o}`]);
    const lampOn = readings.some((r) => r?.output === true);
    const powerW = readings.reduce((sum, r) => sum + (r?.apower ?? 0), 0);
    if (!lampOn) this.#manualAutoOffAt = null;
    this.#setStatus({
      connected: true,
      lampOn,
      lampPowerW: Math.round(powerW * 10) / 10,
      manualAutoOffAt: this.#manualAutoOffAt,
    });
    return { lampOn };
  }

  /** Every lamp output off: through the script (it records why), then each output directly. */
  async #switchOff(sw: SwitchClient, reason: AbortReason): Promise<void> {
    try {
      await sw.script('stop', { reason });
    } catch {
      // The script may be missing or stopped: the direct commands below still switch off.
    }
    const outputs = this.#sync.pack?.robot.outputs ?? [0, 1, 2, 3];
    for (const output of outputs) {
      try {
        await sw.rpc('Switch.Set', { id: output, on: false });
      } catch {
        // Checked below.
      }
    }
    let lampOn = true;
    try {
      ({ lampOn } = await this.#readLamp(sw));
    } catch (error) {
      this.#lostSwitch(error);
    }
    if (lampOn) throw new StopNotAcknowledgedError('The switch did not confirm the lamp is off');
  }

  /** Reads the switch's log until it holds a record for this run, or the time runs out. */
  async #awaitVerdict(runId: string, timeoutMs: number): Promise<SwitchRun | null> {
    const deadline = this.#now() + timeoutMs;
    for (;;) {
      const held = this.#verdicts.get(runId);
      if (held) return held;
      try {
        const sw = await this.#connect(true);
        if (sw) {
          for (const entry of await this.#readLog(sw)) {
            if (entry.run === runId) return entry;
          }
        }
      } catch (error) {
        this.#lostSwitch(error);
      }
      if (this.#now() >= deadline) return null;
      await this.#sleep(1000);
    }
  }

  async #readLog(sw: SwitchClient): Promise<SwitchRun[]> {
    const { body } = await sw.script<LogPage>('log');
    return [...(body?.done ?? [])];
  }

  /**
   * Files the switch's records: a cut for our running cycle ends it (the operator is told why);
   * a completed one waits for completeCycle; anything else (lamp tests, blocked switch-ons, runs
   * from before a reset) becomes an event. Each is acknowledged once stored on the tablet.
   */
  async #collect(sw: SwitchClient): Promise<void> {
    for (let page = 0; page < 5; page++) {
      const entries = await this.#readLog(sw);
      const done: number[] = [];
      for (const entry of entries) {
        const active = this.#active;
        if (active && entry.run === active.id) {
          if (entry.outcome === 'completed') {
            this.#verdicts.set(active.id, entry);
            continue;
          }
          await this.#finish(active, entry, null);
          for (const listener of this.#lostListeners) listener(abortReasonOf(entry));
          continue; // acknowledged by #finish
        }
        if (this.#verdicts.has(entry.run ?? '')) continue;
        if (entry.run && entry.run === this.#lastFinished?.id) {
          done.push(entry.seq); // recorded with its cycle; only the acknowledgement was missing
          continue;
        }
        await this.#event(eventTypeOf(entry), null, entry.run, { ...entry });
        done.push(entry.seq);
      }
      if (done.length === 0) return;
      await sw.script('log', { ack: done.join(',') });
    }
  }

  /** Saves the cycle's record on the tablet, tells the switch it may forget it, and syncs. */
  async #finish(
    active: ActiveRun,
    verdict: SwitchRun | null,
    stoppedFor: AbortReason | null,
  ): Promise<void> {
    if (this.#active?.id !== active.id) return; // recorded already
    // Claimed before the first await: a log read or a stop arriving meanwhile must not record the
    // cycle a second time, nor file its switch record as someone else's run.
    const previous = this.#lastFinished;
    this.#active = null;
    this.#lastFinished = { id: active.id, verdict };
    const body: SyncCycle = {
      id: active.id,
      operatorId: active.operatorId,
      wardCode: active.job.wardCode,
      bed: active.job.bed,
      positionId: active.job.positionId,
      positionLabel: active.job.positionLabel,
      durationSeconds: active.job.durationSeconds,
      wallClockSeconds: active.job.wallClockSeconds,
      channels: [...active.job.channels],
      ...outcomeOf(verdict, stoppedFor),
      startedAt: active.startedAt,
      finishedAt: new Date(this.#now()).toISOString(),
      tabletUptimeMs: active.tabletUptimeMs,
      switchRecordSeq: verdict?.seq ?? null,
      onMs: verdict ? [...verdict.onMs] : [],
    };
    try {
      await this.#store.addToOutbox({
        id: active.id,
        kind: 'cycle',
        state: 'pending',
        createdAt: body.finishedAt,
        body: body as unknown as Record<string, unknown>,
      });
    } catch (error) {
      this.#active = active;
      this.#lastFinished = previous;
      throw error;
    }
    this.#verdicts.delete(active.id);
    await this.#store.remove(ACTIVE_KEY);
    if (verdict && this.#client) {
      await this.#client.script('log', { ack: String(verdict.seq) }).catch(() => undefined);
    }
    this.#setStatus({ ...this.#status, lampOn: false });
    void this.#sync.syncNow();
  }

  async #event(
    type: string,
    operatorId: number | null,
    cycleId: string | null,
    detail: Record<string, unknown>,
  ): Promise<void> {
    const id = this.#uuid();
    const at = new Date(this.#now()).toISOString();
    await this.#store.addToOutbox({
      id,
      kind: 'event',
      state: 'pending',
      createdAt: at,
      body: { id, at, type, operatorId, cycleId, detail },
    });
  }

  #setStatus(next: RobotStatus): void {
    const prev = this.#status;
    const same =
      prev.connected === next.connected &&
      prev.lampOn === next.lampOn &&
      prev.lampPowerW === next.lampPowerW &&
      prev.manualAutoOffAt === next.manualAutoOffAt;
    this.#status = next;
    if (!same) for (const listener of this.#listeners) listener();
  }
}

/** The record's status and reason for the server: completed only on the switch's word. */
function outcomeOf(
  verdict: SwitchRun | null,
  stoppedFor: AbortReason | null,
): Pick<SyncCycle, 'status' | 'reason' | 'reasonDetail'> {
  if (!verdict) {
    return {
      status: stoppedFor ? 'cut' : 'not_confirmed',
      reason: stoppedFor ?? 'NOT_CONFIRMED',
      reasonDetail: 'the switch could not be reached for its record',
    };
  }
  if (verdict.outcome === 'completed')
    return { status: 'completed', reason: null, reasonDetail: null };
  const kept = verdict.reason === 'POWER_LOST' || verdict.reason === 'NOT_WATCHED';
  return {
    status: 'cut',
    reason: kept ? verdict.reason : abortReasonOf(verdict),
    reasonDetail: `${verdict.reason ?? 'UNKNOWN'}: ${verdict.detail ?? ''}`.slice(0, 200),
  };
}

function eventTypeOf(entry: SwitchRun): string {
  if (entry.kind === 'blocked') return 'lamp_blocked';
  if (entry.kind === 'test') return 'lamp_test';
  return 'switch_run_unmatched';
}
